package com.videoanalyzer.web.components.analysis

import androidx.compose.runtime.Composable
import com.videoanalyzer.web.models.ColorInfo
import com.videoanalyzer.web.models.DetectedElements
import com.videoanalyzer.web.models.ThumbnailAnalysis
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Thumbnail analysis section components.
 */

@Composable
fun ThumbnailAnalysisCard(analysis: ThumbnailAnalysis) {
    Div({
        classes("card", "fade-in-right", "stagger-2")
    }) {
        // Header
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.SpaceBetween)
                marginBottom(16.px)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                }
            }) {
                Div({
                    style {
                        width(32.px)
                        height(32.px)
                        borderRadius(6.px)
                        backgroundColor(Color("#27272A"))
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        fontSize(16.px)
                    }
                }) { Text("🖼️") }

                H3({
                    style {
                        fontSize(15.px)
                        fontWeight(600)
                        color(Color("#FAFAFA"))
                    }
                }) { Text("Thumbnail Analysis") }
            }

            ScoreBadge(analysis.overallScore)
        }

        // Thumbnail image with frame
        if (analysis.thumbnailUrl.isNotBlank()) {
            Div({
                style {
                    borderRadius(6.px)
                    overflow("hidden")
                    marginBottom(16.px)
                    property("border", "1px solid #3F3F46")
                }
            }) {
                Img(src = analysis.thumbnailUrl) {
                    style {
                        width(100.percent)
                        display(DisplayStyle.Block)
                    }
                }
            }
        }

        // Score breakdown grid
        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(2, 1fr)")
                gap(8.px)
                marginBottom(16.px)
            }
        }) {
            ThumbnailScoreItem("Face/Expression", analysis.scores.faceScore, "😊")
            ThumbnailScoreItem("Text Overlay", analysis.scores.textScore, "📝")
            ThumbnailScoreItem("Colors", analysis.scores.colorScore, "🎨")
            ThumbnailScoreItem("Composition", analysis.scores.compositionScore, "📐")
        }

        // Detected elements
        DetectedElementsSection(analysis.detectedElements)

        // Suggestions
        if (analysis.suggestions.isNotEmpty()) {
            Div({ style { marginTop(16.px) } }) {
                P({
                    style {
                        fontSize(11.px)
                        fontWeight(600)
                        color(Color("#A1A1AA"))
                        marginBottom(8.px)
                        property("text-transform", "uppercase")
                        letterSpacing(0.3.px)
                    }
                }) { Text("Tips") }

                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(6.px)
                    }
                }) {
                    analysis.suggestions.forEach { suggestion ->
                        SuggestionItem(suggestion)
                    }
                }
            }
        }
    }
}

@Composable
fun ThumbnailScoreItem(label: String, score: Int, icon: String) {
    val color = getScoreColor(score)

    Div({
        style {
            backgroundColor(Color("#27272A"))
            padding(12.px)
            borderRadius(6.px)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(6.px)
                marginBottom(6.px)
            }
        }) {
            Span({ style { fontSize(12.px) } }) { Text(icon) }
            Span({
                style {
                    fontSize(11.px)
                    color(Color("#A1A1AA"))
                }
            }) { Text(label) }
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Baseline)
                gap(2.px)
            }
        }) {
            Span({
                style {
                    fontFamily("'SF Mono', Monaco, monospace")
                    fontSize(22.px)
                    fontWeight(600)
                    color(Color(color))
                }
            }) { Text("$score") }
            Span({
                style {
                    fontSize(11.px)
                    color(Color("#71717A"))
                }
            }) { Text("/100") }
        }
    }
}

@Composable
fun DetectedElementsSection(elements: DetectedElements) {
    Div({
        style {
            backgroundColor(Color("#27272A"))
            padding(12.px)
            borderRadius(6.px)
        }
    }) {
        P({
            style {
                fontSize(10.px)
                color(Color("#71717A"))
                marginBottom(10.px)
                fontWeight(500)
                property("text-transform", "uppercase")
                letterSpacing(0.3.px)
            }
        }) { Text("Detected Elements") }

        // Faces
        if (elements.faces.isNotEmpty()) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexWrap(FlexWrap.Wrap)
                    gap(6.px)
                    marginBottom(10.px)
                }
            }) {
                elements.faces.forEach { face ->
                    Tag("😊 ${face.emotion}", "#3F3F46", "#A1A1AA")
                }
            }
        }

        // Text
        if (elements.text.isNotEmpty()) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexWrap(FlexWrap.Wrap)
                    gap(6.px)
                    marginBottom(10.px)
                }
            }) {
                elements.text.forEach { text ->
                    Tag("\"$text\"", "rgba(34, 197, 94, 0.15)", "#22C55E")
                }
            }
        }

        // Colors
        if (elements.colors.isNotEmpty()) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexWrap(FlexWrap.Wrap)
                    gap(6.px)
                }
            }) {
                elements.colors.take(5).forEach { color ->
                    ColorTag(color)
                }
            }
        }

        if (elements.faces.isEmpty() && elements.text.isEmpty() && elements.colors.isEmpty()) {
            P({
                style {
                    color(Color("#71717A"))
                    fontSize(12.px)
                    fontStyle("italic")
                }
            }) { Text("No notable elements detected") }
        }
    }
}

@Composable
fun Tag(text: String, bgColor: String, textColor: String) {
    Span({
        style {
            backgroundColor(Color(bgColor))
            color(Color(textColor))
            padding(4.px, 8.px)
            borderRadius(4.px)
            fontSize(11.px)
            fontWeight(500)
        }
    }) { Text(text) }
}

@Composable
fun ColorTag(color: ColorInfo) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(6.px)
            backgroundColor(Color("#3F3F46"))
            padding(4.px, 8.px)
            borderRadius(4.px)
        }
    }) {
        Div({
            style {
                width(12.px)
                height(12.px)
                borderRadius(2.px)
                backgroundColor(Color(color.hex))
            }
        })
        Span({
            style {
                fontSize(11.px)
                color(Color("#A1A1AA"))
            }
        }) { Text(color.name) }
    }
}
